import { SupportedLanguage } from '@/constants/language';
import { BlogNewsProp, BlogNewsListItemProp } from '@/services';

const categoriesOf = (
  post: BlogNewsProp | BlogNewsListItemProp,
  locale: SupportedLanguage,
): string[] | null => (locale === 'ko' ? post.categoryKo : post.categoryEn);

/**
 * locale에 따른 카테고리 값 반환
 */
export const getCategoryByLocale = (
  post: BlogNewsProp | BlogNewsListItemProp,
  locale: SupportedLanguage,
): string | undefined => categoriesOf(post, locale)?.[0];

/**
 * 날짜를 "YY. MM. DD" 형식으로 포맷팅
 * @example "2024-01-15T00:00:00" -> "24. 01. 15"
 */
export const formatPostDate = (dateString: string): string => {
  return dateString.split('T')[0].replaceAll('-', '. ').substring(2);
};

/*
 * 아래는 목록·검색·관련글이 공유하는 파이프라인 조각이다. 호출부가
 * `.filter(...).filter(...).sort(byNewest)` 로 이어 붙여 쓴다.
 */

/**
 * 최신순 정렬 비교자.
 *
 * ⚠️ `Array.sort` 는 제자리 정렬이다. 목록 캐시(`getBlogNewsPostsForList`)가 준 배열에
 * 직접 걸면 **캐시된 배열 자체가 재정렬**되어 같은 프로세스의 다른 소비처가 영향을 받는다.
 * 항상 `.filter(...)` 를 한 번이라도 거친 뒤(= 새 배열) 정렬한다.
 */
export const byNewest = (
  a: BlogNewsListItemProp,
  b: BlogNewsListItemProp,
): number => new Date(b.date).getTime() - new Date(a.date).getTime();

export const supportingLocale =
  (locale: SupportedLanguage) =>
  (post: BlogNewsListItemProp): boolean =>
    post.lang?.includes(locale) ?? false;

/**
 * 빈 쿼리는 전건 통과다 — 호출부가 빈 검색어를 막지 않으므로 그때는 필터가 없는 것처럼
 * 동작해야 한다.
 */
export const matchingQuery = (query: string) => {
  const lower = query.toLowerCase();

  return (post: BlogNewsListItemProp): boolean =>
    post.title.toLowerCase().includes(lower) ||
    (post.slug?.toLowerCase().includes(lower) ?? false);
};

/**
 * 카테고리 필터 한 칸 — `'all'` 은 "필터 없음"을 뜻한다.
 * 목록·검색이 같은 규칙을 써야 하므로 조건식을 여기서 소유한다.
 */
export const inCategory =
  (category: string, locale: SupportedLanguage) =>
  (post: BlogNewsListItemProp): boolean =>
    category === 'all' ||
    categoriesOf(post, locale)?.includes(category) ||
    false;

/**
 * `'all'` 을 특별 취급하지 않는 카테고리 일치 — 관련 글에서만 쓴다.
 *
 * ⚠️ 관련 글 호출부(`news/[slug]/page.tsx`)는 카테고리가 없는 글에 `|| 'all'` 폴백을
 * 넣는데, 여기서 `'all'` 은 필터 해제가 아니라 카테고리명이라 **관련 글이 0건이 된다**.
 * 폴백 의도와 어긋나므로 손대려면 그 호출부와 함께 판단해야 한다.
 */
const inExactCategory =
  (category: string, locale: SupportedLanguage) =>
  (post: BlogNewsListItemProp): boolean =>
    categoriesOf(post, locale)?.includes(category) || false;

/**
 * 관련 포스트 필터링 및 정렬
 */
export const getRelatedPosts = (
  allPosts: BlogNewsListItemProp[],
  currentSlug: string,
  category: string,
  locale: SupportedLanguage,
  limit: number = 6,
): BlogNewsListItemProp[] =>
  allPosts
    .filter(supportingLocale(locale))
    .filter(inExactCategory(category, locale))
    .filter(post => post.slug !== currentSlug)
    .sort(byNewest)
    .slice(0, limit);

/** 그 로케일의 그 카테고리 글, 최신순 */
export const postsInCategory = (
  posts: BlogNewsListItemProp[],
  category: string,
  locale: SupportedLanguage,
): BlogNewsListItemProp[] =>
  posts
    .filter(supportingLocale(locale))
    .filter(inCategory(category, locale))
    .sort(byNewest);

/** 그 로케일에서 검색어에 걸리는 글, 최신순 */
export const postsMatchingQuery = (
  posts: BlogNewsListItemProp[],
  query: string,
  locale: SupportedLanguage,
): BlogNewsListItemProp[] =>
  posts
    .filter(supportingLocale(locale))
    .filter(matchingQuery(query))
    .sort(byNewest);

export const calculateTotalPages = (
  totalItems: number,
  postsPerPage: number,
): number => {
  if (totalItems === 0) return 1;
  return Math.ceil(totalItems / postsPerPage);
};

export const countPostsByCategory = (
  posts: BlogNewsListItemProp[],
  category: string,
  locale: SupportedLanguage,
): number =>
  posts.filter(supportingLocale(locale)).filter(inCategory(category, locale))
    .length;
